import type { AvatarId } from "./avatar.ts";

/** Crockford Base32: no I, L, O, U, so codes survive hand copying. */
export const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

export const RECOVERY_CODE_LENGTH = 20; // 20 * 5 bits = 100 bits of entropy
export const PUBLIC_TAG_SUFFIX_LENGTH = 4;

const DISPLAY_NAME_MAX = 20;
const DISPLAY_NAME_MIN = 2;

export function encodeCrockford(bytes: Uint8Array, length: number): string {
  let bits = 0;
  let value = 0;
  let out = "";
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5 && out.length < length) {
      out += CROCKFORD_ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
    value &= (1 << bits) - 1;
  }
  if (out.length < length) {
    throw new RangeError("not enough random bytes for requested length");
  }
  return out;
}

export function formatRecoveryCode(raw: string): string {
  return raw.match(/.{1,5}/g)?.join("-") ?? raw;
}

/**
 * Accepts the code the way people type it: lowercase, spaces, dashes,
 * and the classic O/0 and I/L/1 confusions. Returns null when it cannot
 * possibly be a valid code, so the server can skip hashing garbage.
 */
export function normalizeRecoveryCode(input: string): string | null {
  const cleaned = input
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
  if (cleaned.length !== RECOVERY_CODE_LENGTH) {
    return null;
  }
  for (const char of cleaned) {
    if (!CROCKFORD_ALPHABET.includes(char)) {
      return null;
    }
  }
  return cleaned;
}

const TAG_PREFIX: Record<AvatarId, string> = {
  wizard: "MAGE",
  sorceress: "MOON",
  princess: "ROSE",
  prince: "LION",
  "angel-boy": "HALO",
  "angel-girl": "DOVE",
  "demon-boy": "HORN",
  "demon-girl": "RUBY",
};

export function publicTagFor(avatarId: AvatarId, suffix: string): string {
  return `${TAG_PREFIX[avatarId]}-${suffix}`;
}

export const DEFAULT_NAME_BY_AVATAR: Record<AvatarId, string> = {
  wizard: "Pháp Sư",
  sorceress: "Pháp Sư",
  princess: "Công Chúa",
  prince: "Hoàng Tử",
  "angel-boy": "Thiên Thần",
  "angel-girl": "Thiên Thần",
  "demon-boy": "Ác Quỷ",
  "demon-girl": "Ác Quỷ",
};

/**
 * Cleans a player-chosen nickname: Unicode letters (Vietnamese included),
 * digits, spaces and a few separators. Markup, control and invisible
 * characters are dropped. Returns null when nothing usable remains.
 */
export function sanitizeDisplayName(input: string): string | null {
  const normalized = input.normalize("NFC");
  const kept = Array.from(normalized)
    .filter((char) => /[\p{L}\p{M}\p{N} ._-]/u.test(char))
    .join("")
    .replace(/\s+/g, " ")
    .trim();

  const chars = Array.from(kept);
  if (chars.length < DISPLAY_NAME_MIN) {
    return null;
  }
  const clipped = chars.slice(0, DISPLAY_NAME_MAX).join("").trim();
  if (/(.)\1{5,}/u.test(clipped)) {
    return null;
  }
  if (/(https?|www\.|\.com|\.net|\.vn)/i.test(clipped)) {
    return null;
  }
  return Array.from(clipped).length >= DISPLAY_NAME_MIN ? clipped : null;
}
